import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo
} from "react";
import type { CubePage } from "../hooks/useCubeSelectionState";
import { useSavedViewList } from "../savedViews/useSavedViewList";
import type {
  AggView,
  PlanByMode,
  SummaryView
} from "../utils/summary/summaryPageHelpers";
import {
  BUILT_IN_SUMMARY_VIEWS,
  DEFAULT_BUILT_IN_SUMMARY_VIEW
} from "./builtInSummaryViews";
import {
  ALL_SUMMARY_VIEW_SECTIONS,
  captureSummaryViewPayload,
  isSummaryViewDirty,
  parseSummaryViewPayload,
  sectionsOfSummaryView,
  type SummaryViewLive,
  type SummaryViewPayload,
  type SummaryViewSections
} from "./summaryViewPayload";

/** The display controls a view sets and reads. */
export interface SummaryDisplayState {
  category: SummaryView | null;
  setCategory: Dispatch<SetStateAction<SummaryView | null>>;
  agg: AggView;
  setAgg: Dispatch<SetStateAction<AggView>>;
  planBy: PlanByMode;
  setPlanBy: Dispatch<SetStateAction<PlanByMode>>;
  visibility: Record<string, boolean>;
  setVisibility: Dispatch<SetStateAction<Record<string, boolean>>>;
}

/** Inputs the hook needs to drive the summary bar and grid. */
export interface UseSummaryViewStateParams {
  /** The page whose views these are — its saved-view list and its scope. */
  page: Extract<CubePage, "forecast-summary" | "budget-summary">;
  /** Live display controls. */
  display: SummaryDisplayState;
  /** Live table and row dimension. */
  dims: {
    tableDim: string;
    setTableDim: Dispatch<SetStateAction<string>>;
    rowDim: string;
    setRowDim: Dispatch<SetStateAction<string>>;
  };
}

/**
 * The summary's Views layer: which view is applied, whether the page has
 * drifted from it, and the user's own views for this summary. Same shape as
 * the temporal grid's ``useViewState``; only what a view sets differs.
 */
export function useSummaryViewState({
  page,
  display,
  dims
}: UseSummaryViewStateParams) {
  const { setPlanBy, setAgg, setCategory, setVisibility } = display;
  const { setTableDim, setRowDim } = dims;
  const live = useMemo(
    (): SummaryViewLive => ({
      planBy: display.planBy,
      agg: display.agg,
      category: display.category,
      visibility: display.visibility,
      tableDim: dims.tableDim,
      rowDim: dims.rowDim
    }),
    [
      display.planBy,
      display.agg,
      display.category,
      display.visibility,
      dims.tableDim,
      dims.rowDim
    ]
  );

  // A view sets the columns as a whole: the seed and the hand-set choices it
  // carries replace whatever the planner had opened.
  const applyPayload = useCallback(
    (payload: SummaryViewPayload) => {
      const shape = payload.dataShape;
      if (shape?.planBy) setPlanBy(shape.planBy);
      if (shape?.agg) setAgg(shape.agg);
      if (payload.columns) {
        setCategory(payload.columns.category);
        setVisibility(payload.columns.visibility ?? {});
      }
      if (payload.dims) {
        setTableDim(payload.dims.tableDim);
        setRowDim(payload.dims.rowDim);
      }
    },
    [setPlanBy, setAgg, setCategory, setVisibility, setTableDim, setRowDim]
  );

  const capture = useCallback(
    (sections: SummaryViewSections) =>
      JSON.stringify(captureSummaryViewPayload(sections, live)),
    [live]
  );

  const list = useSavedViewList({
    page,
    builtIns: BUILT_IN_SUMMARY_VIEWS,
    defaultBuiltIn: DEFAULT_BUILT_IN_SUMMARY_VIEW,
    parse: parseSummaryViewPayload,
    applyPayload,
    readyToOpen: true,
    capture,
    sectionsOf: sectionsOfSummaryView,
    allSections: ALL_SUMMARY_VIEW_SECTIONS
  });

  return {
    ...list,
    isDirty: list.activePayload
      ? isSummaryViewDirty(list.activePayload, live)
      : false
  };
}

/** The summary's Views layer, as the page and its controls consume it. */
export type SummaryViewState = ReturnType<typeof useSummaryViewState>;
